/**
 * Phone Statistics Tab
 * Phone + source_id from Voluum (groupBy custom-variable-9, custom-variable-5).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui';
import { useAuth, useCountry, usePhonePerformanceTags, usePhonesWithStats } from '@/hooks';
import {
  aggregateVoluum,
  buildPhoneServerExportSortOptions,
  CampaignStatsDashboard,
  TruncatedPhoneCell,
  FILTER_FIELDS_PHONE,
  GROUP_BY_OPTIONS_PHONE,
  toRangeISO,
  toGenericStatsRowFromPhone,
  useStatsTabState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { FiltersState } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared';

import { PhoneStatsDatasetLimitBanner } from './PhoneStatsDatasetLimitBanner';
import {
  cloneFiltersState,
  phoneStatsAggregateFiltersFingerprint,
  usePhoneStatsFilteredAggregate,
  usePhoneStatsServerCsvExport,
} from './hooks';

import type { PhonesWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { StatsTableFullDatasetAggregate } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/table';

interface PhoneStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
}

export const PhoneStatsTab: FC<PhoneStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const country = useCountry(countryKey);
  const state = useStatsTabState(workDate, 'phone');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const perfTagsQuery = usePhonePerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByPhoneKey = perfTagsQuery.data?.tagsByPhoneKey ?? {};
  const tagDetailsByPhoneKey = perfTagsQuery.data?.tagDetailsByPhoneKey ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const query = usePhonesWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
  });

  const allModePrevLenRef = useRef<number | null>(null);
  const [appliedFullDatasetFilters, setAppliedFullDatasetFilters] =
    useState<FiltersState | null>(null);
  const [phoneCsvExcludeCrossSource, setPhoneCsvExcludeCrossSource] =
    useState(false);

  useEffect(() => {
    if (state.length !== 0) {
      allModePrevLenRef.current = state.length;
      setAppliedFullDatasetFilters(null);
      return;
    }
    const prev = allModePrevLenRef.current;
    allModePrevLenRef.current = 0;
    if (prev !== 0) {
      setAppliedFullDatasetFilters(cloneFiltersState(state.filtersState));
    }
  }, [state.length, state.filtersState]);

  const aggregateFiltersForFullDataset = useMemo(() => {
    if (state.length !== 0) return state.filtersState;
    return appliedFullDatasetFilters ?? state.filtersState;
  }, [state.length, state.filtersState, appliedFullDatasetFilters]);

  const fullDatasetFilterPending = useMemo(() => {
    if (state.length !== 0 || appliedFullDatasetFilters === null) return false;
    return (
      phoneStatsAggregateFiltersFingerprint(state.filtersState) !==
      phoneStatsAggregateFiltersFingerprint(appliedFullDatasetFilters)
    );
  }, [state.length, state.filtersState, appliedFullDatasetFilters]);

  const fullAggregate = usePhoneStatsFilteredAggregate({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    filtersState: aggregateFiltersForFullDataset,
    excludeCrossSourceNotedRows: phoneCsvExcludeCrossSource,
    phonesDataUpdatedAt: query.dataUpdatedAt,
    enabled: state.length === 0 && query.isSuccess,
  });

  const fullDatasetAggregate = useMemo((): StatsTableFullDatasetAggregate | undefined => {
    if (state.length !== 0) return undefined;
    if (query.isError) {
      return { loading: false, error: true, rowCount: null, totals: null };
    }
    if (!query.isSuccess) return undefined;
    return {
      loading: fullAggregate.isLoading || fullAggregate.isFetching,
      error: fullAggregate.isError,
      rowCount: fullAggregate.data?.filteredRowCount ?? null,
      totals: fullAggregate.data?.totals ?? null,
    };
  }, [
    state.length,
    query.isError,
    query.isSuccess,
    fullAggregate.isLoading,
    fullAggregate.isFetching,
    fullAggregate.isError,
    fullAggregate.data,
  ]);

  const refetchPhoneDashboard = useCallback(() => {
    void query.refetch();
    void fullAggregate.refetch();
  }, [query, fullAggregate]);

  const allPhoneRows = query.data?.rows ?? [];
  const allGenericRows = useMemo(() => {
    const byKey = new Map<string, PhonesWithStatsRow[]>();
    for (const r of allPhoneRows) {
      const key = `${(r.phone ?? '').trim()}::${(r.sourceId ?? '').trim()}`;
      const list = byKey.get(key) ?? [];
      list.push(r);
      byKey.set(key, list);
    }
    const deduped: PhonesWithStatsRow[] = [];
    for (const group of byKey.values()) {
      const first = group[0]!;
      const merged =
        group.length > 1
          ? { ...first, voluum: aggregateVoluum(group) }
          : first;
      deduped.push(merged);
    }
    return deduped.map((r, i) => {
      const base = toGenericStatsRowFromPhone(r, i);
      const phoneKey = base.id;
      const tags =
        phoneKey
          ? (tagsByPhoneKey[phoneKey] ??
            Object.entries(tagsByPhoneKey).find(
              ([k]) => k.toLowerCase() === phoneKey.toLowerCase(),
            )?.[1]) ?? []
          : [];
      return { ...base, performanceTags: tags };
    });
  }, [allPhoneRows, tagsByPhoneKey]);

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    allGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const totals = query.data?.totals ?? {};

  const tagDetailsLookup = useCallback(
    (phoneKey: string) => {
      if (!phoneKey) return undefined;
      const direct = tagDetailsByPhoneKey[phoneKey];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByPhoneKey).find(
        ([k]) => k.toLowerCase() === phoneKey.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByPhoneKey],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const phoneKey = row.id;
      const performanceTags = phoneKey
        ? (tagsByPhoneKey[phoneKey] ??
          Object.entries(tagsByPhoneKey).find(
            ([k]) => k.toLowerCase() === phoneKey.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = phoneKey ? tagDetailsLookup(phoneKey) : undefined;
      return (
        <TruncatedPhoneCell
          phone={row.label ?? '—'}
          sourceId={row.subLabel ?? row._originalPhone?.sourceId}
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
        />
      );
    },
    [tagsByPhoneKey, tagDetailsLookup, tagColors],
  );

  const totalRowCount =
    query.data?.totalRows ?? query.data?.total ?? allGenericRows.length;

  const cap = query.data?.dataCappedAt;
  const showCapNotice =
    cap != null && (query.data?.totalRows ?? 0) > cap;

  const serverCsvExport = usePhoneStatsServerCsvExport({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    filtersState: state.filtersState,
    sort: state.sort,
  });

  const phoneCsvSortOptions = useMemo(() => buildPhoneServerExportSortOptions(), []);
  /** defaultSort null: server CSV streams Voluum pages without buffering entire dataset to sort. */
  const phoneCsvSortConfig = useMemo(
    () => ({ options: phoneCsvSortOptions, defaultSort: null }),
    [phoneCsvSortOptions],
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {showCapNotice && query.data?.totalRows != null ? (
        <PhoneStatsDatasetLimitBanner maxLoaded={cap} totalMatching={query.data.totalRows} />
      ) : null}
    <CampaignStatsDashboard
      tab="phone"
      title="Phone"
      emptyMessage="No phone data found for this range."
      loadingTitle="Loading phone performance data"
      loadingDesc="Fetching Voluum metrics grouped by phone + source."
      errorPrefix="Failed to load phone stats."
      firstColumnLabel="Phone | Source"
      firstColumnSortKey="phone"
      rowUnitLabel="row"
      filterFields={FILTER_FIELDS_PHONE}
      groupByOptions={GROUP_BY_OPTIONS_PHONE}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={totalRowCount}
      allRowsLength={allGenericRows.length}
      currency={query.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={query.isError ?? false}
      error={query.error}
      isLoading={query.isLoading ?? false}
      isFetching={query.isFetching ?? false}
      dataUpdatedAt={query.dataUpdatedAt}
      refetch={refetchPhoneDashboard}
      showLimitSelect
      allowAllLimit
      showGroupBySelect={false}
      csvExportUserId={user?.id}
      csvExportContext={{
        smswiserCountryId: country.data?.countryId,
        countryKey,
      }}
      serverCsvExport={state.length === 0 ? serverCsvExport : undefined}
      serverCsvSortConfig={phoneCsvSortConfig}
      csvExportWorkDate={workDate}
      serverCsvExportEstimatedRowCount={
        state.length === 0 ? (query.data?.totalRows ?? totalRowCount) : undefined
      }
      fullDatasetAggregate={fullDatasetAggregate}
      fullDatasetFiltersStale={
        state.length === 0 &&
        fullDatasetFilterPending &&
        !fullAggregate.isLoading &&
        !fullAggregate.isFetching
      }
      controlledExcludeCrossSourceNotedRows={
        state.length === 0 ? phoneCsvExcludeCrossSource : undefined
      }
      onControlledExcludeCrossSourceNotedRowsChange={
        state.length === 0 ? setPhoneCsvExcludeCrossSource : undefined
      }
      filterToolbarExtra={
        state.length === 0 ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 shrink-0 whitespace-nowrap"
            disabled={!fullDatasetFilterPending}
            onClick={() =>
              setAppliedFullDatasetFilters(cloneFiltersState(state.filtersState))
            }
          >
            Apply to full dataset
          </Button>
        ) : undefined
      }
    />
    </div>
  );
};

export default PhoneStatsTab;
